import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { useUser } from '@application/authentication/use_user';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { validateNin } from '@application/validators';
import { CellulaFormSubmitData, Flex, Form, InputRadio, InputText, Text, useFormContext } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { RepresentablePatientsPicker } from '@feature/representation/representable-patients-picker.component';
import { usePatientRepresentation } from '@feature/representation/use-patient-representation';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { withNewContainer } from '@visiba/instantiation';
import { type ChangeEvent } from 'react';
import { CreateNewMessageAnalyticActions } from '../../hooks/use-create-new-message';
import { useScreenWhoIsRespondent } from './use-screen-who-is-respondent';

type RepresentablePatient = ReturnType<typeof useRepresentablePatients>['representablePatients'][number];
type ScreenWhoIsRespondentData = ReturnType<typeof useScreenWhoIsRespondent>['data'];

export const ScreenWhoIsRespondent = withNewContainer(function ScreenWhoIsRespondent() {
	const { representablePatients, currentlyRepresentedPatient, isLoading } = useRepresentablePatients();
	const { data, submit, allowRepresentative } = useScreenWhoIsRespondent();
	const analytic = useAnalyticContext();

	function handleSubmit(data: CellulaFormSubmitData) {
		const selectedRepresentablePatient =
			data.respondentType === 'representative'
				? representablePatients.find((person) => person.Id === data.representablePatientId)
				: null;

		// When "Patient med ombud" is inactive the patient can only act as a representative by
		// selecting a verified representable person (which triggers a redirect). Block the manual
		// "Next" path until such a person is selected.
		if (!allowRepresentative && data.respondentType === 'representative' && !selectedRepresentablePatient) {
			return {
				ok: null,
				err: I18nAndL10n.translate('View.Booking.ScreenWhoIsRespondant.ValidationError.Required.RepresentablePatient'),
			};
		}

		submit({
			respondentType: selectedRepresentablePatient?.Id ?? data.respondentType,
			representativeNin: selectedRepresentablePatient ? null : (data.representativeNin ?? null),
			representablePatientId: selectedRepresentablePatient?.Id ?? null,
			asAgentFor: selectedRepresentablePatient?.IdentityNumber ?? null,
		});
	}

	function handleInvalidSubmit() {
		analytic.trackEvent(CreateNewMessageAnalyticActions.FormIsInvalid);
	}

	if (currentlyRepresentedPatient) {
		return null;
	}

	return (
		<>
			<TopBar.Content goBack />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Form.Root
					options={{
						defaultValues: {
							respondentType: data.respondentType,
							representablePatientId: 'representablePatientId' in data ? data.representablePatientId : null,
							representativeNin: data.representativeNin,
						},
						mode: 'onBlur',
					}}
					onSubmit={handleSubmit}
					onInvalid={handleInvalidSubmit}
				>
					<Flex grow={1} gap={tokens.spacing.x2}>
						<Title title={withTranslation('View.Booking.ScreenWhoIsRespondant.Title')} titleSize='small' />
						<ScreenWhoIsRespondentFormInputs
							data={data}
							allowRepresentative={allowRepresentative}
							representablePatients={currentlyRepresentedPatient ? undefined : representablePatients}
						/>
					</Flex>

					<Flex gap={tokens.spacing.x2}>
						<Form.Error />
						<Form.SubmitButton loading={isLoading} onClick={() => analytic.trackEvent('Clicked next button')}>
							{withTranslation('PW.Common.Next')}
						</Form.SubmitButton>
					</Flex>
				</Form.Root>
			</Flex>
		</>
	);
});

function ScreenWhoIsRespondentFormInputs({
	data,
	allowRepresentative,
	representablePatients,
}: {
	data: ScreenWhoIsRespondentData;
	allowRepresentative: boolean;
	representablePatients?: RepresentablePatient[];
}) {
	const analytic = useAnalyticContext();
	const formContext = useFormContext();
	const respondentType = formContext?.control.watch('respondentType');
	const representablePatientId = formContext?.control.watch('representablePatientId');
	const isRepresentingSomeoneElse = respondentType === 'representative';
	const { startRepresentation } = usePatientRepresentation();
	const user = useUser();

	function handleRespondentTypeChange(value?: string) {
		if (value === 'me') {
			formContext?.control.setValue('representablePatientId', null, { shouldDirty: true, shouldValidate: true });
			formContext?.control.setValue('representativeNin', null, { shouldDirty: true, shouldValidate: true });
		}

		analytic.trackEvent(`Changed "RespondentType" to "${value}"`);
	}

	function handleRepresentativeNinChange() {
		analytic.trackEvent(`Changed "RepresentativeNin"`);
	}

	function handleRepresentativeNinInput(event: ChangeEvent<HTMLInputElement>) {
		if (event.target.value.trim()) {
			formContext?.control.setValue('representablePatientId', null, { shouldDirty: true, shouldValidate: true });
		}
	}

	function validateRepresentativeNin(value: string) {
		const hasSelectedRepresentablePatient = formContext?.control.getValues('representablePatientId') != null;

		if (hasSelectedRepresentablePatient) {
			return true;
		}

		if (!value?.trim()) {
			return I18nAndL10n.translate('Form.ValidationError.Required.Nin');
		}

		return validateNin(data.currentCountry, value) || I18nAndL10n.translate('Form.ValidationError.Format.Nin');
	}

	const myselfBaseLabel = I18nAndL10n.translate('View.Booking.ScreenWhoIsRespondant.Option.Me');
	const myselfLabel = user?.commonName ? `${myselfBaseLabel} (${user.commonName})` : myselfBaseLabel;

	return (
		<Flex gap={tokens.spacing.x2}>
			<InputRadio.Root
				label={withTranslation('View.Booking.ScreenWhoIsRespondant.Input.Label.WhoIsRespondant')}
				variant='outlined'
				required
				name='respondentType'
				options={{
					required: I18nAndL10n.translate('View.Booking.ScreenWhoIsRespondant.ValidationError.Required.WhoIsRespondant'),
				}}
				onChange={handleRespondentTypeChange}
			>
				<InputRadio.Item label={myselfLabel} value='me' />
				<InputRadio.Item label={withTranslation('View.Booking.ScreenWhoIsRespondant.Option.Representative')} value='representative' />
			</InputRadio.Root>
			{isRepresentingSomeoneElse ? (
				<Flex gap={tokens.spacing.x2}>
					{representablePatients && representablePatients.length > 0 ? (
						<RepresentablePatientsPicker
							representablePatients={representablePatients}
							onSelect={(person) => startRepresentation(person.Id)}
							hiddenInputName='representablePatientId'
							showOrDivider={allowRepresentative}
						/>
					) : null}
					{allowRepresentative ? (
						<>
							<InputText
								label={withTranslation('Form.Input.Label.Nin')}
								name='representativeNin'
								required={representablePatientId == null}
								description={withTranslation('View.Booking.ScreenWhoIsRespondant.RepresentativeNin.Description')}
								placeholder={data.representativeNinPlaceholder}
								onBlur={handleRepresentativeNinChange}
								onChange={handleRepresentativeNinInput}
								options={{
									validate: {
										nin: validateRepresentativeNin,
									},
								}}
							/>
							<Text variant='labelSmallRegular' color={tokens.color.content.muted}>
								{withTranslation('View.Booking.ScreenWhoIsRespondant.RepresentativeNin.Hint')}
							</Text>
						</>
					) : null}
				</Flex>
			) : null}
		</Flex>
	);
}
